import { differenceInDays } from "date-fns";
import type {
  LifeStyleModel,
  PrescriptionModel,
  UserStatsModel,
  VitalsModel,
} from "../types/models";
import { VitalsStatusEnum } from "../constants/enums";
import { parseBackendDate } from "./date";

export type VitalMetricKey =
  | "sleep_hours"
  | "steps_count"
  | "blood_systolic_pressure"
  | "blood_diastolic_pressure"
  | "blood_glucose"
  | "heart_rate";

/**
 * Only return a metric when it looks like a real logged reading.
 * Prefer field_sources provenance; otherwise ignore placeholder zeros.
 */
export const getLoggedMetricValue = (
  stats: UserStatsModel | null | undefined,
  key: VitalMetricKey
): number | null => {
  if (!stats) return null;
  const raw = stats[key];
  if (typeof raw !== "number" || Number.isNaN(raw)) return null;

  const sources = stats.field_sources;
  const hasProvenance =
    !!sources &&
    typeof sources === "object" &&
    Object.keys(sources).length > 0;

  if (hasProvenance) {
    return sources[key] ? raw : null;
  }

  // Legacy rows without provenance: 0 is an empty placeholder, not Critical.
  if (raw === 0) return null;
  return raw;
};

export const getVitalsBgColor = (status?: VitalsStatusEnum) => {
  switch (status) {
    case VitalsStatusEnum.GOOD:
      return "rgba(10, 190, 117, 0.3)";
    case VitalsStatusEnum.WARNING:
      return "rgba(251, 148, 0, 0.3)";
    case VitalsStatusEnum.DANGER:
      return "rgba(246, 85, 84, 0.3)";
    default:
      return "rgba(158, 158, 158, 0.3)";
  }
};

export const getTransparentBgColor = (status?: VitalsStatusEnum) => {
  switch (status) {
    case VitalsStatusEnum.GOOD:
      return "rgba(10, 190, 117, 0.1)";
    case VitalsStatusEnum.WARNING:
      return "rgba(251, 148, 0, 0.1)";
    case VitalsStatusEnum.DANGER:
      return "rgba(246, 85, 84, 0.1)";
    default:
      return "rgba(158, 158, 158, 0.1)";
  }
};

export const getIconColor = (status?: VitalsStatusEnum) => {
  switch (status) {
    case VitalsStatusEnum.GOOD:
      return "rgba(10, 190, 117, 1)";
    case VitalsStatusEnum.WARNING:
      return "rgba(251, 148, 0, 1)";
    case VitalsStatusEnum.DANGER:
      return "rgba(246, 85, 84, 1)";
    default:
      return "rgba(158, 158, 158, 1)";
  }
};

const isOutOfRange = (value: number, range: [number, number]): boolean =>
  value < range[0] || value > range[1];

const FRESH_DAYS = 2;

const isTimestampFresh = (value?: string | null) => {
  if (!value) return false;
  const parsed = parseBackendDate(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return differenceInDays(new Date(), parsed) <= FRESH_DAYS;
};

/** Same 2-day freshness rule as mobile — grey L/V/P when stats are stale. */
export const isStatsFresh = (user_stats?: UserStatsModel | null) => {
  if (!user_stats) return false;

  const sources = user_stats.field_sources;
  if (sources && typeof sources === "object") {
    const capturedAts = Object.values(sources)
      .map((source) =>
        source && typeof source === "object" ? source.captured_at : null
      )
      .filter(Boolean) as string[];
    if (capturedAts.length > 0) {
      return capturedAts.some((capturedAt) => isTimestampFresh(capturedAt));
    }
  }

  return isTimestampFresh(user_stats.updated_at);
};

export const getFreshMetricValue = (
  stats: UserStatsModel | null | undefined,
  key: VitalMetricKey
): number | null => {
  if (!isStatsFresh(stats)) return null;
  return getLoggedMetricValue(stats, key);
};

export const getLifeStyleStatus = (
  plan_lifestyle: LifeStyleModel,
  user_stats?: UserStatsModel
): VitalsStatusEnum | undefined => {
  if (!user_stats || !plan_lifestyle || !isStatsFresh(user_stats)) {
    return undefined;
  }

  const entries = Object.entries(plan_lifestyle).flatMap(([key, ranges]) => {
    const userValue = getLoggedMetricValue(user_stats, key as VitalMetricKey);
    if (userValue === null) return [];
    return [{ userValue, ranges }];
  });

  if (entries.length === 0) return undefined;

  for (const { userValue, ranges } of entries) {
    if (
      isOutOfRange(userValue, ranges.warning) &&
      isOutOfRange(userValue, ranges.good)
    ) {
      return VitalsStatusEnum.DANGER;
    }
  }

  for (const { userValue, ranges } of entries) {
    if (isOutOfRange(userValue, ranges.good)) {
      return VitalsStatusEnum.WARNING;
    }
  }

  return VitalsStatusEnum.GOOD;
};

export const getVitalStatus = (
  plan_vitals: VitalsModel,
  user_stats?: UserStatsModel
): VitalsStatusEnum | undefined => {
  if (!user_stats || !plan_vitals || !isStatsFresh(user_stats)) {
    return undefined;
  }

  const entries = Object.entries(plan_vitals).flatMap(([key, ranges]) => {
    const userValue = getLoggedMetricValue(user_stats, key as VitalMetricKey);
    if (userValue === null) return [];
    return [{ userValue, ranges }];
  });

  if (entries.length === 0) return undefined;

  for (const { userValue, ranges } of entries) {
    if (
      isOutOfRange(userValue, ranges.warning) &&
      isOutOfRange(userValue, ranges.good)
    ) {
      return VitalsStatusEnum.DANGER;
    }
  }

  for (const { userValue, ranges } of entries) {
    if (isOutOfRange(userValue, ranges.good)) {
      return VitalsStatusEnum.WARNING;
    }
  }

  return VitalsStatusEnum.GOOD;
};

export const getPrescriptionStatus = (
  plan_prescriptions: PrescriptionModel[],
  user_stats?: UserStatsModel
): VitalsStatusEnum | undefined => {
  const planPrescriptions = Array.isArray(plan_prescriptions)
    ? plan_prescriptions
    : [];
  const userPrescriptionStats = Array.isArray(user_stats?.prescription)
    ? user_stats.prescription
    : [];

  if (!user_stats || !planPrescriptions.length || !isStatsFresh(user_stats)) {
    return undefined;
  }

  const allTaken = planPrescriptions.every((prescription) =>
    userPrescriptionStats.find(
      (stat) => stat.name === prescription.name && stat.taken
    )
  );

  return allTaken ? VitalsStatusEnum.GOOD : VitalsStatusEnum.DANGER;
};
